import { useTranslation } from "react-i18next";

import { Column, Columns, FloatingLabelInput, FloatingLabelSelect } from "@aviary";
import { l } from "@shared/locales/i18n";
import type { BankAccountInfoFieldValues, BankInformationErrors } from "@shared/types/fields";

import { AccountNumberField } from "../AccountNumberField/AccountNumberField";
import { RoutingNumberField } from "../RoutingNumberField/RoutingNumberField";

interface Props {
  values: BankAccountInfoFieldValues;
  errors: BankInformationErrors["fields"];
  onChange: (values: BankAccountInfoFieldValues) => void;
}

const BankAccountInfoFields = ({ values, errors, onChange }: Props) => {
  const { t } = useTranslation();

  const handleOnChange = e => {
    onChange({
      ...values,
      [e.target.id]: e.target.value,
    });
  };

  const routingAndAccountNumFields = () => {
    return (
      <>
        <Column columnWidth={6}>
          <RoutingNumberField
            value={values?.routingNumber ?? ""}
            errors={errors?.routingNumber}
            onChange={handleOnChange}
          />
        </Column>

        <Column columnWidth={6}>
          <AccountNumberField
            value={values?.accountNumber}
            errors={errors?.accountNumber}
            onChange={handleOnChange}
          />
        </Column>
      </>
    );
  };

  const accountNameAndHeldByFields = () => {
    return (
      <>
        <Column columnWidth={6}>
          <FloatingLabelInput
            data-testid="account-holder-input"
            type="text"
            id="accountHolder"
            value={values?.accountHolder ?? ""}
            label={t(l.sharedOnboarding.bankingInfo.accountHolderName)}
            required
            maxLength={50}
            handleChange={handleOnChange}
            errors={errors?.accountHolder}
            data-e2e="account-holder"
          />
        </Column>

        <Column columnWidth={6}>
          <FloatingLabelSelect
            data-testid="account-held-by-input"
            type="text"
            id="accountHeldBy"
            label={t(l.sharedOnboarding.bankingInfo.accountHeldBy)}
            value={values?.accountHeldBy ?? ""}
            required
            handleChange={handleOnChange}
            errors={errors?.accountHeldBy}
          >
            <option value="individual">{t(l.sharedOnboarding.bankingInfo.individual)}</option>
            <option value="company">{t(l.sharedOnboarding.bankingInfo.company)}</option>
          </FloatingLabelSelect>
        </Column>
      </>
    );
  };

  return (
    <Columns isMultiline>
      {accountNameAndHeldByFields()}
      {routingAndAccountNumFields()}
      <Column columnWidth={6}>
        <FloatingLabelSelect
          type="text"
          id="accountType"
          value={values?.accountType ?? ""}
          label={t(l.sharedOnboarding.bankingInfo.accountType)}
          required
          handleChange={handleOnChange}
          errors={errors?.accountType}
        >
          <option value="checking">{t(l.sharedOnboarding.bankingInfo.checking)}</option>
          <option value="savings">{t(l.sharedOnboarding.bankingInfo.savings)}</option>
        </FloatingLabelSelect>
      </Column>
    </Columns>
  );
};

export { BankAccountInfoFields };
export type { BankAccountInfoFieldValues };
